import { ModalManager } from '@mui/material/Modal';

const canFocus = element => element?.isConnected && typeof element.focus === 'function'
    && !element.closest('[aria-hidden="true"], [inert]') && !element.disabled;

const focusModal = modal => {
    const surface = modal?.modalRef?.querySelector('[role="dialog"], [role="menu"]');
    if (canFocus(surface)) {
        if (!surface.hasAttribute('tabindex')) surface.setAttribute('tabindex', '-1');
        surface.focus();
    }
};

// MUI's focus trap restores focus on close, before Dialog's exit transition
// releases aria-hidden on the page. Restore it at manager removal instead.
// Keep MUI's normal focus trap, scroll locking and modal stacking intact.
export class AccessibleModalManager extends ModalManager {
    constructor() {
        super();
        this.returnTargets = new Map();
    }

    add(modal, container) {
        if (this.modals.includes(modal)) return super.add(modal, container);
        const active = container.ownerDocument.activeElement;
        const previous = this.modals[this.modals.length - 1];
        // Keep the earlier trigger as a fallback for menu -> dialog handoffs.
        this.returnTargets.set(modal, [active, ...(this.returnTargets.get(previous) || [])]);
        if (active && active !== container.ownerDocument.body
            && !modal.modalRef?.contains(active)) active.blur();
        const index = super.add(modal, container);
        focusModal(modal);
        return index;
    }

    remove(modal, ariaHiddenState = true) {
        const wasTop = this.isTopModal(modal);
        const targets = this.returnTargets.get(modal) || [];
        const active = modal.modalRef?.ownerDocument.activeElement;
        // Never mark the closing dialog hidden while its button retains focus.
        if (modal.modalRef?.contains(active)) active.blur();
        const index = super.remove(modal, ariaHiddenState);
        this.returnTargets.delete(modal);
        if (index !== -1 && wasTop) {
            const target = targets.find(canFocus);
            if (target) target.focus();
            else focusModal(this.modals[this.modals.length - 1]);
        }
        return index;
    }
}

// One stack across themes: changing theme must not reset open overlays.
export default new AccessibleModalManager();
